import { useState, useEffect, useRef, FormEvent } from 'react';
import { aboutInfo } from '@/mocks/about';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value && typeof value === 'string' && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });
      const response = await fetch('https://readdy.ai/api/form/d9pefg6b4qjhggaepshg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '073漫画网',
    url: 'https://073comic.com',
    description: aboutInfo.introduction,
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '长沙市',
      addressRegion: '湖南省',
      addressCountry: 'CN',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于 073漫画网
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.slogan}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.value}</p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cozy%20office%20workspace%20with%20comic%20books%20and%20art%20on%20walls%2C%20warm%20ambient%20lighting%2C%20creative%20atmosphere%2C%20illustration%20studio%20vibe%2C%20clean%20aesthetic%2C%20editorial%20photography%20style%2C%20natural%20light%20from%20windows&width=600&height=400&seq=about-brand-story&orientation=landscape"
                  alt="073漫画网品牌故事"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              核心价值观
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">我们的承诺与坚守</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.values.map((v) => (
                <div key={v.title} className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">听听读者们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">快速了解073漫画网</p>
            <div className="flex flex-col gap-3">
              {aboutInfo.faqs.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电话</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">地址</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  留言反馈
                </h2>
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言，最多500字"
                    ></textarea>
                  </div>
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-accent-600">{formError}</p>
                  )}
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">感谢您的留言！我们会尽快回复您。</p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}